// Consolidated PAS2161 layer model (tokens + parser + labels).
import type { DefectLayerPresentation } from '../contracts/availableLayers';

export const PAS2161_WORKSPACE = 'wfs_layers' as const;
export const PAS2161_SURVEY_YEAR = '2026-27' as const;

/** Single GeoServer client  */
export const PAS2161_CLIENTS = ['pas-2161'] as const;
export type Pas2161Client = (typeof PAS2161_CLIENTS)[number];

/** Tenant slug that owns PAS2161 curated overlays. */
export const PAS2161_OVERLAY_TENANT_SLUG: Pas2161Client = 'pas-2161';

/** Match `m-mfv` before `mfv` when parsing. */
export const PAS2161_SURVEY_TYPES = ['m-mfv', 'mfv'] as const;
export type Pas2161SurveyType = (typeof PAS2161_SURVEY_TYPES)[number];

/** Geographic regions encoded in GeoServer run tokens. */
export const PAS2161_REGIONS = ['chesh', 'lincs', 'notts'] as const;
export type Pas2161Region = (typeof PAS2161_REGIONS)[number];

/** Pass variants within each region. */
export const PAS2161_PASSES = ['main', 'repeat1', 'repeat2'] as const;
export type Pas2161Pass = (typeof PAS2161_PASSES)[number];

/**
 * Region × pass runs
 * chesh / lincs / notts × main / repeat1 / repeat2.
 */
export const PAS2161_RUNS = [
  'chesh-main',
  'chesh-repeat1',
  'chesh-repeat2',
  'lincs-main',
  'lincs-repeat1',
  'lincs-repeat2',
  'notts-main',
  'notts-repeat1',
  'notts-repeat2',
] as const;
export type Pas2161Run = (typeof PAS2161_RUNS)[number];

export function pas2161RunFor(
  region: Pas2161Region,
  pass: Pas2161Pass,
): Pas2161Run {
  return `${region}-${pass}` as Pas2161Run;
}

export function pas2161RunsForRegion(
  region: Pas2161Region,
): readonly Pas2161Run[] {
  return PAS2161_PASSES.map((pass) => pas2161RunFor(region, pass));
}

export function formatPas2161RegionLabel(region: Pas2161Region): string {
  switch (region) {
    case 'chesh':
      return 'Cheshire';
    case 'lincs':
      return 'Lincolnshire';
    case 'notts':
      return 'Nottinghamshire';
  }
}

export const PAS2161_AGGREGATIONS_M = [100, 50, 10, 5] as const;
export type Pas2161AggregationM = (typeof PAS2161_AGGREGATIONS_M)[number];

/** Mini MFV defect codes  */
export const PAS2161_MINI_DEFECTS = [
  'd00',
  'd10',
  'd20',
  'd40',
  'p00',
  'i00',
] as const;
export type Pas2161MiniDefect = (typeof PAS2161_MINI_DEFECTS)[number];

/**
 * Longer names first so accred-defect-grid wins over defect-grid.
 */
export const PAS2161_MINI_LAYER_NAMES = [
  'defect-grid-d00',
  'defect-grid-d10',
  'defect-grid-d20',
  'defect-grid-d40',
  'defect-grid-p00',
  'defect-grid-i00',
] as const;
export type Pas2161MiniLayerName = (typeof PAS2161_MINI_LAYER_NAMES)[number];

export const PAS2161_MFV_LAYER_NAMES = [
  'accred-defect-grid',
  'defect-grid',
] as const;
export type Pas2161MfvLayerName = (typeof PAS2161_MFV_LAYER_NAMES)[number];
export type Pas2161LayerName = Pas2161MiniLayerName | Pas2161MfvLayerName;

/** All layer-name tokens, longest first (for parse matching). */
export const PAS2161_LAYER_NAMES_LONGEST_FIRST: readonly Pas2161LayerName[] = [
  ...PAS2161_MINI_LAYER_NAMES,
  ...PAS2161_MFV_LAYER_NAMES,
].sort((left, right) => right.length - left.length);

export interface Pas2161DefectMeta {
  readonly code: Pas2161MiniDefect;
  readonly name: string;
  readonly presentation: DefectLayerPresentation;
  readonly catalogCode: string;
}

export const PAS2161_MINI_DEFECT_META: Readonly<
  Record<Pas2161MiniDefect, Pas2161DefectMeta>
> = {
  d00: {
    code: 'd00',
    name: 'Longitudinal cracks',
    presentation: 'crackmap',
    catalogCode: 'D00',
  },
  d10: {
    code: 'd10',
    name: 'Transversal cracks',
    presentation: 'crackmap',
    catalogCode: 'D10',
  },
  d20: {
    code: 'd20',
    name: 'Alligator cracks',
    presentation: 'crackmap',
    catalogCode: 'D20',
  },
  d40: {
    code: 'd40',
    name: 'Potholes',
    presentation: 'pothole',
    catalogCode: 'D40',
  },
  p00: {
    code: 'p00',
    name: 'Patches',
    presentation: 'crackmap',
    catalogCode: 'P00',
  },
  i00: {
    code: 'i00',
    name: 'Iron work',
    presentation: 'crackmap',
    catalogCode: 'I00',
  },
};

export function isPas2161Client(value: string): value is Pas2161Client {
  return (PAS2161_CLIENTS as readonly string[]).includes(value);
}

export function isPas2161Run(value: string): value is Pas2161Run {
  return (PAS2161_RUNS as readonly string[]).includes(value);
}

export function isPas2161MiniDefect(value: string): value is Pas2161MiniDefect {
  return (PAS2161_MINI_DEFECTS as readonly string[]).includes(value);
}

export function isPas2161MfvLayerName(
  value: string,
): value is Pas2161MfvLayerName {
  return (PAS2161_MFV_LAYER_NAMES as readonly string[]).includes(value);
}

export function isPas2161LayerName(value: string): value is Pas2161LayerName {
  return (PAS2161_LAYER_NAMES_LONGEST_FIRST as readonly string[]).includes(
    value,
  );
}

export function toPas2161MiniLayerName(
  defect: Pas2161MiniDefect,
): Pas2161MiniLayerName {
  return `defect-grid-${defect}` as Pas2161MiniLayerName;
}

export function miniDefectFromLayerName(
  layerName: Pas2161LayerName,
): Pas2161MiniDefect | null {
  if (!layerName.startsWith('defect-grid-')) {
    return null;
  }
  const code = layerName.slice('defect-grid-'.length);
  return isPas2161MiniDefect(code) ? code : null;
}

export function formatPas2161AggregationToken(
  aggregationM: Pas2161AggregationM,
): string {
  return `${aggregationM}mgrid`;
}

export function parsePas2161AggregationToken(
  token: string,
): Pas2161AggregationM | null {
  const match = /^(\d+)mgrid$/.exec(token);
  if (!match) {
    return null;
  }
  const metres = Number(match[1]);
  if (metres === 5 || metres === 10 || metres === 50 || metres === 100) {
    return metres;
  }
  return null;
}

export function formatPas2161ClientLabel(_client: Pas2161Client = 'pas-2161'): string {
  return 'PAS2161';
}

export function formatPas2161RegionClientLabel(region: Pas2161Region): string {
  return formatPas2161RegionLabel(region);
}

export function matchPas2161TokenPrefix(
  haystack: string,
  candidates: readonly string[],
): { readonly token: string; readonly rest: string } | null {
  const sorted = [...candidates].sort((left, right) => right.length - left.length);
  for (const candidate of sorted) {
    if (haystack === candidate) {
      return { token: candidate, rest: '' };
    }
    const prefix = `${candidate}-`;
    if (haystack.startsWith(prefix)) {
      return { token: candidate, rest: haystack.slice(prefix.length) };
    }
  }
  return null;
}

export interface Pas2161LayerRef {
  readonly workspace: typeof PAS2161_WORKSPACE;
  readonly client: Pas2161Client;
  readonly surveyType: Pas2161SurveyType;
  readonly run: Pas2161Run;
  readonly layerName: Pas2161LayerName;
  readonly aggregationM: Pas2161AggregationM | null;
  readonly surveyYear: typeof PAS2161_SURVEY_YEAR;
}

function stripWorkspace(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) {
    return null;
  }
  const separator = trimmed.indexOf(':');
  if (separator === -1) {
    return trimmed;
  }
  const workspace = trimmed.slice(0, separator);
  const localName = trimmed.slice(separator + 1);
  if (workspace !== PAS2161_WORKSPACE || !localName) {
    return null;
  }
  return localName;
}

function matchSurveyType(
  middle: string,
): { readonly surveyType: Pas2161SurveyType; readonly rest: string } | null {
  for (const surveyType of PAS2161_SURVEY_TYPES) {
    const prefix = `${surveyType}-`;
    if (middle.startsWith(prefix)) {
      return { surveyType, rest: middle.slice(prefix.length) };
    }
  }
  return null;
}

function matchLayerName(
  surveyType: Pas2161SurveyType,
  layerAndMaybeAgg: string,
): {
  readonly layerName: Pas2161LayerName;
  readonly aggregationM: Pas2161AggregationM | null;
} | null {
  let aggregationM: Pas2161AggregationM | null = null;
  let layerToken = layerAndMaybeAgg;

  const lastDash = layerAndMaybeAgg.lastIndexOf('-');
  if (lastDash > 0) {
    const maybeAgg = layerAndMaybeAgg.slice(lastDash + 1);
    const parsedAgg = parsePas2161AggregationToken(maybeAgg);
    if (parsedAgg != null) {
      aggregationM = parsedAgg;
      layerToken = layerAndMaybeAgg.slice(0, lastDash);
    }
  }

  const allowed =
    surveyType === 'm-mfv' ? PAS2161_MINI_LAYER_NAMES : PAS2161_MFV_LAYER_NAMES;

  for (const candidate of PAS2161_LAYER_NAMES_LONGEST_FIRST) {
    if (candidate !== layerToken) {
      continue;
    }
    if (!(allowed as readonly string[]).includes(candidate)) {
      return null;
    }
    if (!isPas2161LayerName(candidate)) {
      return null;
    }
    return { layerName: candidate, aggregationM };
  }

  return null;
}

/**
 * Parse a PAS2161 GeoServer layer id 
 * `{pas-2161}-{m-mfv|mfv}-{chesh|lincs|notts}-{main|repeat1|repeat2}-{defect-grid[-code]|accred-defect-grid}-[Nmgrid]-{year}`
 */
export function parsePas2161LayerId(input: string): Pas2161LayerRef | null {
  if (typeof input !== 'string' || !input.trim()) {
    return null;
  }

  const localName = stripWorkspace(input);
  if (localName == null) {
    return null;
  }

  const yearSuffix = `-${PAS2161_SURVEY_YEAR}`;
  if (!localName.endsWith(yearSuffix)) {
    return null;
  }
  const withoutYear = localName.slice(0, -yearSuffix.length);
  if (!withoutYear) {
    return null;
  }

  const clientMatch = matchPas2161TokenPrefix(withoutYear, PAS2161_CLIENTS);
  if (!clientMatch || !isPas2161Client(clientMatch.token)) {
    return null;
  }

  const surveyMatch = matchSurveyType(clientMatch.rest);
  if (!surveyMatch) {
    return null;
  }

  const runMatch = matchPas2161TokenPrefix(surveyMatch.rest, PAS2161_RUNS);
  if (!runMatch || !isPas2161Run(runMatch.token) || !runMatch.rest) {
    return null;
  }

  const layerMatch = matchLayerName(surveyMatch.surveyType, runMatch.rest);
  if (!layerMatch) {
    return null;
  }

  return {
    workspace: PAS2161_WORKSPACE,
    client: clientMatch.token,
    surveyType: surveyMatch.surveyType,
    run: runMatch.token,
    layerName: layerMatch.layerName,
    aggregationM: layerMatch.aggregationM,
    surveyYear: PAS2161_SURVEY_YEAR,
  };
}

export function formatPas2161LocalName(ref: Pas2161LayerRef): string {
  const parts: string[] = [
    ref.client,
    ref.surveyType,
    ref.run,
    ref.layerName,
  ];
  if (ref.aggregationM != null) {
    parts.push(formatPas2161AggregationToken(ref.aggregationM));
  }
  parts.push(ref.surveyYear);
  return parts.join('-');
}

export function formatPas2161LayerId(ref: Pas2161LayerRef): string {
  return `${PAS2161_WORKSPACE}:${formatPas2161LocalName(ref)}`;
}

export function toRawLayerRef(ref: Pas2161LayerRef): Pas2161LayerRef {
  if (ref.aggregationM == null) {
    return ref;
  }
  return { ...ref, aggregationM: null };
}

export function withAggregation(
  ref: Pas2161LayerRef,
  aggregationM: Pas2161AggregationM,
): Pas2161LayerRef {
  return { ...ref, aggregationM };
}

function layerNamesForSurveyType(
  surveyType: Pas2161SurveyType,
): readonly Pas2161LayerName[] {
  return surveyType === 'm-mfv'
    ? PAS2161_MINI_LAYER_NAMES
    : PAS2161_MFV_LAYER_NAMES;
}

/**
 * Full expected inventory for fixtures.
 * 1 client × 8 datasets × 9 runs × (1 raw + 4 grids) = 360.
 */
export function listExpectedLayerRefs(): readonly Pas2161LayerRef[] {
  const refs: Pas2161LayerRef[] = [];
  for (const client of PAS2161_CLIENTS) {
    for (const surveyType of PAS2161_SURVEY_TYPES) {
      for (const run of PAS2161_RUNS) {
        for (const layerName of layerNamesForSurveyType(surveyType)) {
          const base: Pas2161LayerRef = {
            workspace: PAS2161_WORKSPACE,
            client,
            surveyType,
            run,
            layerName,
            aggregationM: null,
            surveyYear: PAS2161_SURVEY_YEAR,
          };
          refs.push(base);
          for (const aggregationM of PAS2161_AGGREGATIONS_M) {
            refs.push(withAggregation(base, aggregationM));
          }
        }
      }
    }
  }
  return refs;
}

export function isPas2161LayerId(input: string): boolean {
  return parsePas2161LayerId(input) != null;
}

export interface Pas2161OverlayLabels {
  readonly loadingLabel: string;
  readonly overviewLoadingLabel: string;
}

function mfvLoadingLabel(layerName: Pas2161MfvLayerName): string {
  return layerName === 'accred-defect-grid'
    ? 'Cracking (Accredited)'
    : 'Cracking (Relaxed)';
}

export function resolvePas2161OverlayLabels(
  sourceLayerId: string | null | undefined,
): Pas2161OverlayLabels | null {
  const parsed = parsePas2161LayerId(sourceLayerId ?? '');
  if (!parsed) {
    return null;
  }

  const miniDefect = miniDefectFromLayerName(parsed.layerName);
  if (miniDefect) {
    const name = PAS2161_MINI_DEFECT_META[miniDefect].name;
    return { loadingLabel: name, overviewLoadingLabel: `${name} Grid` };
  }
  if (!isPas2161MfvLayerName(parsed.layerName)) {
    return null;
  }
  const name = mfvLoadingLabel(parsed.layerName);
  return { loadingLabel: name, overviewLoadingLabel: `${name} Grid` };
}
